import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, FileTextIcon, DollarSignIcon, CheckCircleIcon, AlertTriangleIcon, MoreHorizontalIcon, EyeIcon, EditIcon, ArchiveIcon, MailIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../../../shared/types/Invoice';
export function Invoices() {
  const {
    t
  } = useTranslation('invoices');
  const navigate = useNavigate();
  // Currency comes from the central tenant config, not a hardcoded string
  const currencyCode = getTenant().currency.code;
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Mock data
  const invoices: Invoice[] = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: getTenant().currency.code,
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    items: [],
    subtotal: 125000,
    total: 125000
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    customerName: 'Tech Solutions Ltd',
    customerEmail: 'accounts@techsolutions.mv',
    customerPhone: '+60 11 234 5678',
    amount: 87500,
    currency: getTenant().currency.code,
    status: 'sent',
    dueDate: '2024-02-01',
    issueDate: '2024-01-15',
    description: 'Consulting services',
    items: [],
    subtotal: 87500,
    total: 87500
  }, {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    customerName: 'Global Enterprises',
    customerEmail: 'finance@global.com',
    amount: 250000,
    currency: getTenant().currency.code,
    status: 'overdue',
    dueDate: '2024-01-20',
    issueDate: '2024-01-05',
    description: 'Product delivery',
    items: [],
    subtotal: 250000,
    total: 250000
  }, {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    customerName: 'Innovation Hub',
    customerEmail: 'billing@innovationhub.mv',
    amount: 45000,
    currency: getTenant().currency.code,
    status: 'draft',
    dueDate: '2024-02-15',
    issueDate: '2024-01-25',
    description: 'Software license',
    items: [],
    subtotal: 45000,
    total: 45000
  }];
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-7555" data-mp="1kidhgj93o0u5">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchQuery === '' || invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeTab === 'all' || invoice.status === activeTab;
    return matchesSearch && matchesStatus;
  });
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = filteredInvoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-7556" data-mp="2z1rbwqfbhqqr">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-7557" data-mp="2kcv855kamzvy">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-7558" data-mp="2p9bze8mshud9">
          {t('pages.Invoices.invoices')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-7559" data-mp="2bbn9unhs1z1v">
          <button onClick={() => navigate('/invoice-form')} className="px-4 py-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-7560" data-mp="2fbxqcu3747uz">
            {t('pages.Invoices.add_invoice')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-7561" data-mp="25pmrvqc4o4d4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-7562" data-mp="20oz195rt9pjf">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-7563" data-mp="2flpsusv8twyh">
            <FileTextIcon size={16} className="text-gray-500" data-id="element-7564" data-mp="2anxzdueya2s6" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-7565" data-mp="2allm3uk50glg">
              {t('pages.Invoices.total_invoices')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-7566" data-mp="2keal3pyi41zw">
            <div className="text-2xl font-bold text-gray-900" data-id="element-7567" data-mp="2kflo3nk4h5py">
              {filteredInvoices.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-7568" data-mp="2tep7ksn7bn4z">
            {t('pages.Invoices.all_time')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-7569" data-mp="1qyezcnqvl479">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-7570" data-mp="1v00e6emyzsk3">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-7571" data-mp="1zzv4oh2u2g4m" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-7572" data-mp="24sy250aqe9q3">
              {t('pages.Invoices.total_amount')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-7573" data-mp="29tbz9nasnz9c">
            <div className="text-2xl font-bold text-gray-900" data-id="element-7574" data-mp="2ep16mct22kg7">
              {currencyCode} {totalAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-7575" data-mp="2nqscpirictal">
            {t('pages.Invoices.across')} {filteredInvoices.length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-7576" data-mp="tby4rq9j14p7">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-7577" data-mp="lu9ywc5bbza5">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-7578" data-mp="h28e21u2x08i" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-7579" data-mp="c3eze632336r">
              {t('pages.Invoices.paid')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-7580" data-mp="75di9qp6xdxx">
            <div className="text-2xl font-bold text-green-600" data-id="element-7581" data-mp="72hirxxp2h7d">
              {currencyCode} {paidAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-7582" data-mp="zuaxtm20owmz">
            {filteredInvoices.filter(i => i.status === 'paid').length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-7583" data-mp="qunv8wxjxksc">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-7584" data-mp="3fhsw0w6y4e70">
            <AlertTriangleIcon size={16} className="text-red-500" data-id="element-7585" data-mp="3bkn3l7iidvvh" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-7586" data-mp="1eh4sonam1x87">
              {t('pages.Invoices.overdue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-7587" data-mp="15h7wm0lw5w34">
            <div className="text-2xl font-bold text-red-600" data-id="element-7588" data-mp="15i9kj5o0ad29">
              {currencyCode} {overdueAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-red-50 text-xs text-red-700 font-medium" data-id="element-7589" data-mp="1y65fo5xxfwwx">
            {filteredInvoices.filter(i => i.status === 'overdue').length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-7590" data-mp="1ofkjsq9n16hp">
        {/* Tabs + Controls */}
        <div className="flex items-end justify-between border-b border-gray-200" data-id="element-7591" data-mp="1teng9mbjy4e2">
          <div className="flex space-x-6" role="tablist" aria-label="Invoices" data-id="element-7592" data-mp="2h518pdy1u596">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'draft',
            label: 'Draft'
          }, {
            id: 'sent',
            label: 'Sent'
          }, {
            id: 'paid',
            label: 'Paid'
          }, {
            id: 'overdue',
            label: 'Overdue'
          }, {
            id: 'cancelled',
            label: 'Cancelled'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`invoices-panel-${tab.id}`} id={`invoices-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[var(--brand-primary)]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7593" data-mp="289u0bplq1iaz">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center gap-4 pb-3" data-id="element-7594" data-mp="2wqugpp7yttol">
            <div className="relative" data-id="element-7595" data-mp="2wrclja8p1g84">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-7596" data-mp="2s04bcbzjhqtw" />

              <input type="search" aria-label={t('pages.Invoices.attrs.search_invoices')} placeholder={t('pages.Invoices.attrs.search_invoices')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-7597" data-mp="2n0hwxz7z64ji" />
            </div>
            <label className="flex items-center cursor-pointer" data-id="element-7598" data-mp="2asi3m79uiqt">
              <div className="relative" data-id="element-7599" data-mp="2blgnzw2u1xv">
                <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-7600" data-mp="as2pvxzih9bu" />

                <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[var(--brand-primary)]' : 'bg-gray-200'}`} data-id="element-7601" data-mp="jl5ynsz7oy05" />

                <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-7602" data-mp="3nyul57z6nau1" />
              </div>
              <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-7603" data-mp="sli9b0p8ld8">
                {t('pages.Invoices.show_archived')}
              </span>
            </label>
          </div>
        </div>

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-7604" data-mp="1h7msgo8qkth5">
            <p className="text-gray-500 text-sm" data-id="element-7605" data-mp="rjr4vjv7dubp">
              {t('pages.Invoices.no_invoices_found_matching_your_criteria')}
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-7606" data-mp="mkejl3jzcvb8">
            <table className="w-full text-left border-collapse" data-id="element-7607" data-mp="11h5izctnm70u">
              <tbody className="divide-y divide-gray-100" data-id="element-7608" data-mp="wjvmxebrex41">
                {filteredInvoices.map(invoice => <tr key={invoice.id} onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-7609" data-mp="8u1c12b03imf">
                    <td className="px-5 py-4" data-id="element-7610" data-mp="ikvurwp1g034">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-7611" data-mp="drb6wxh5smhn">
                        {invoice.customerName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-7612" data-mp="3m5tdeurgbtz5">
                        {invoice.invoiceNumber}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-7613" data-mp="1wpfsm60uaak3">
                      <div className="text-sm font-medium text-gray-900" data-id="element-7614" data-mp="21hh6fwdno9c8">
                        {currencyCode}{' '}
                        {invoice.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-7615" data-mp="2fh8pwcmxyhxm">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-7616" data-mp="2kg35l5pm3qdi">
                      <div className="text-sm text-gray-500" data-id="element-7617" data-mp="26hd7pnuyxmo3">
                        {t('pages.Invoices.due_on')}{' '}
                        {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                      </div>
                    </td>
                    <td className="px-4 py-4 w-12 text-right relative" data-id="element-7618" data-mp="v0di50009lxr">
                      <div ref={contextMenuOpenId === invoice.id ? contextMenuRef : null} data-id="element-7619" data-mp="z41ppion0hj0">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === invoice.id ? null : invoice.id);
                  }} aria-label="Invoice actions" data-id="element-7620" data-mp="1ahvdjl3w77jb">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-7621" data-mp="1kbkhi98rpduh" />
                        </button>

                        {contextMenuOpenId === invoice.id && <>
                            <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-7622" data-mp="8wp3mphy3gez" />

                            <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-7623" data-mp="2y8t2fd3e7vvq">
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/invoice-detail/${invoice.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-7624" data-mp="337e5rldxyvoe">
                                <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-7625" data-mp="1mte70dqu1c2q" />

                                {t('pages.Invoices.details')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/invoice-form?id=${invoice.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-7626" data-mp="2z59p12ygxzkr">
                                <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-7627" data-mp="3hv7ynnhx00qi" />

                                {t('pages.InvoiceDetail.edit')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-7628" data-mp="3mwnijc8cw17b">
                                <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-7629" data-mp="5b3ri5yltmkz" />

                                {t('pages.Invoices.send_email')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-7630" data-mp="3dt9p6vqvd4e1">
                                <DownloadIcon size={16} className="mr-3 text-gray-400" data-id="element-7631" data-mp="3p8wk2eknggmv" />

                                {t('pages.Invoices.download_pdf')}
                              </button>
                              <div className="border-t border-gray-100 my-1" data-id="element-7632" data-mp="3ac5h5vbromsi" />
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-7633" data-mp="369ilfjbz6hq1">
                                <ArchiveIcon size={16} className="mr-3" data-id="element-7634" data-mp="2mlry2ub5tya2" />
                                {t('pages.InvoiceDetail.archive')}
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}